import { useState, useEffect, useRef, useId } from "react";
import { ChevronDown, ChevronUp, Check, X, Lock } from "lucide-react";

export type DropdownOption = { id: number; name: string };

export function MultiSelectDropdown({
  id,
  options,
  selected,
  onChange,
  placeholder,
  loading,
  maxCount,
  disabled,
  disabledHint,
  lockedIds,
}: {
  id?: string;
  options: DropdownOption[];
  selected: number[];
  onChange: (ids: number[]) => void;
  placeholder: string;
  loading?: boolean;
  maxCount?: number;
  disabled?: boolean;
  disabledHint?: string;
  lockedIds?: number[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const optionsId = useId();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const atLimit = maxCount !== undefined && selected.length >= maxCount;

  // Membership checks run per option on every render.
  const selectedSet = new Set(selected);
  const lockedSet = new Set(lockedIds ?? []);
  const isLocked = (id: number) => lockedSet.has(id);

  const toggle = (id: number) => {
    if (disabled || isLocked(id)) return;
    if (selectedSet.has(id)) {
      onChange(selected.filter((x) => x !== id));
    } else if (!atLimit) {
      onChange([...selected, id]);
    }
  };

  const removeChip = (e: React.MouseEvent, id: number) => {
    if (disabled || isLocked(id)) return;
    e.stopPropagation();
    onChange(selected.filter((x) => x !== id));
  };

  const selectedOptions = options.filter((o) => selectedSet.has(o.id));

  return (
    <div
      ref={ref}
      onKeyDown={(e) => {
        if (e.key === "Escape" && open) {
          e.stopPropagation();
          setOpen(false);
          trigger.current?.focus();
        }
      }}
      className="relative"
      title={disabled ? disabledHint : undefined}
    >
      <div
        className={`et-space-select-control relative w-full min-h-[48px] px-4 py-2 rounded-xl border text-left flex items-center gap-2 flex-wrap transition-colors bg-white ${
          disabled
            ? "border-gray-100 bg-gray-50 cursor-not-allowed opacity-60"
            : open
              ? "border-[#28543D] ring-2 ring-[#28543D]/20"
              : "border-gray-200"
        }`}
      >
        <button
          ref={trigger}
          id={id}
          aria-label={id ? undefined : placeholder}
          aria-expanded={open}
          aria-controls={open ? optionsId : undefined}
          disabled={disabled || loading}
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="absolute inset-0 w-full rounded-[inherit] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#28543D]"
        />
        {selectedOptions.length === 0 ? (
          <span className="relative pointer-events-none text-sm font-mulish text-gray-300 flex-1">
            {loading ? "Loading…" : placeholder}
          </span>
        ) : (
          <div className="relative pointer-events-none flex flex-wrap gap-1.5 flex-1">
            {selectedOptions.map((o) => (
              <span
                key={o.id}
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700 text-sm font-mulish"
              >
                {o.name}
                {!disabled && !isLocked(o.id) && (
                  <button
                    type="button"
                    aria-label={`Remove ${o.name}`}
                    disabled={loading}
                    onClick={(e) => removeChip(e, o.id)}
                    className="pointer-events-auto text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
        {!disabled && (
          <span className="relative pointer-events-none text-[#28543D] ml-auto shrink-0">
            {open ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </span>
        )}
      </div>

      {open && !loading && options.length > 0 && (
        <div
          id={optionsId}
          className="absolute z-50 top-full mt-1 w-full bg-white border border-gray-100 rounded-xl shadow-lg py-1 max-h-64 overflow-y-auto"
        >
          {options.map((o) => {
            const checked = selectedSet.has(o.id);
            return (
              <button
                key={o.id}
                type="button"
                aria-pressed={checked}
                disabled={disabled || isLocked(o.id) || (!checked && atLimit)}
                onClick={() => toggle(o.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 transition-colors text-left ${
                  isLocked(o.id)
                    ? "cursor-not-allowed"
                    : !checked && atLimit
                      ? "opacity-40 cursor-not-allowed"
                      : "hover:bg-gray-50"
                }`}
              >
                <span
                  className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${
                    isLocked(o.id)
                      ? "bg-gray-100 border-gray-200"
                      : checked
                        ? "bg-[#28543D] border-[#28543D]"
                        : "border-gray-300"
                  }`}
                >
                  {isLocked(o.id) ? (
                    <Lock className="w-3 h-3 text-gray-400" />
                  ) : (
                    checked && (
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    )
                  )}
                </span>
                <span
                  className={`text-sm font-mulish ${isLocked(o.id) ? "text-gray-400" : "text-gray-800"}`}
                >
                  {o.name}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {open && !loading && options.length === 0 && (
        <div
          id={optionsId}
          role="status"
          className="absolute z-50 top-full mt-1 w-full bg-white border border-gray-100 rounded-xl shadow-lg px-4 py-3 text-sm font-mulish text-gray-400"
        >
          No options available.
        </div>
      )}
    </div>
  );
}
